// frontend/src/components/taxi/VrpRouteMap.jsx
import React, { useRef, useEffect, useMemo, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Box, Chip, Stack, Typography, Paper, IconButton, Tooltip } from "@mui/material";
import LayersIcon from "@mui/icons-material/Layers";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const DEFAULT_CENTER = [-71.537, -16.398];
const DEFAULT_ZOOM = 12;

const isValidCoordinate = (lat, lng) => {
    return (
        lat !== undefined &&
        lng !== undefined &&
        lat !== null &&
        lng !== null &&
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180
    );
};

const ROUTE_COLORS = [
    "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
    "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf",
    "#aec7e8", "#ffbb78", "#98df8a", "#ff9896", "#c5b0d5",
];

const VrpRouteMap = ({
    routes = [],
    origin,
    destinations = [],
    selectedRouteIndices = [],
    onToggleRoute,
}) => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const [mapLoaded, setMapLoaded] = useState(false);

    // Calcular centro
    const center = useMemo(() => {
        const allCoords = [];
        if (origin && isValidCoordinate(origin.lat, origin.lng)) {
            allCoords.push([origin.lng, origin.lat]);
        }
        destinations.forEach(d => {
            if (isValidCoordinate(d.lat, d.lng)) {
                allCoords.push([d.lng, d.lat]);
            }
        });
        routes.forEach(route => {
            if (route.stops) {
                route.stops.forEach(stop => {
                    if (isValidCoordinate(stop.lat, stop.lng)) {
                        allCoords.push([stop.lng, stop.lat]);
                    }
                });
            }
        });
        if (allCoords.length === 0) return DEFAULT_CENTER;
        const lngs = allCoords.map(c => c[0]);
        const lats = allCoords.map(c => c[1]);
        return [
            (Math.min(...lngs) + Math.max(...lngs)) / 2,
            (Math.min(...lats) + Math.max(...lats)) / 2,
        ];
    }, [routes, origin, destinations]);

    // Inicializar mapa
    useEffect(() => {
        if (!MAPBOX_TOKEN || !mapContainer.current) return;
        mapboxgl.accessToken = MAPBOX_TOKEN;
        if (!map.current) {
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: "mapbox://styles/mapbox/streets-v12",
                center: center,
                zoom: DEFAULT_ZOOM,
            });
            map.current.on("load", () => {
                setMapLoaded(true);
                map.current.addControl(new mapboxgl.NavigationControl(), "top-right");
                map.current.addControl(new mapboxgl.ScaleControl(), "bottom-right");
                map.current.addControl(new mapboxgl.GeolocateControl({
                    positionOptions: { enableHighAccuracy: true },
                    trackUserLocation: true,
                }), "top-right");
            });
            map.current.on("error", (e) => console.error("Mapbox error:", e));
        }
        return () => {
            if (map.current) {
                map.current.remove();
                map.current = null;
                setMapLoaded(false);
            }
        };
    }, [center]);

    // Dibujar rutas
    useEffect(() => {
        if (!map.current || !mapLoaded) return;
        const mapInstance = map.current;

        // Limpiar capas y fuentes anteriores
        try {
            const style = mapInstance.getStyle();
            if (!style) return;
            const layersToRemove = style.layers.filter(l => l.id.startsWith("vrp-")).map(l => l.id);
            layersToRemove.forEach(id => { try { if (mapInstance.getLayer(id)) mapInstance.removeLayer(id); } catch (e) { } });
            if (style.sources) {
                Object.keys(style.sources).forEach(id => {
                    if (id.startsWith("vrp-")) {
                        try { if (mapInstance.getSource(id)) mapInstance.removeSource(id); } catch (e) { }
                    }
                });
            }
        } catch (e) { console.warn("Error limpiando capas:", e); }

        const validRoutes = routes.filter(route =>
            route.stops && route.stops.length >= 2 &&
            route.stops.some(stop => isValidCoordinate(stop.lat, stop.lng))
        );

        if (validRoutes.length === 0) return;

        validRoutes.forEach((route, index) => {
            const coordinates = route.stops
                .filter(stop => isValidCoordinate(stop.lat, stop.lng))
                .map(stop => [stop.lng, stop.lat]);
            if (coordinates.length < 2) return;

            const color = ROUTE_COLORS[index % ROUTE_COLORS.length];
            const sourceId = `vrp-route-${index}`;
            const layerId = `vrp-route-line-${index}`;
            const isSelected = selectedRouteIndices.includes(index);
            const opacity = isSelected ? 1.0 : 0.25;
            const width = isSelected ? 6 : 3;

            try {
                mapInstance.addSource(sourceId, {
                    type: "geojson",
                    data: {
                        type: "Feature",
                        geometry: { type: "LineString", coordinates },
                        properties: { routeIndex: index, vehicleCount: route.total_passengers || 0 },
                    },
                });
                mapInstance.addLayer({
                    id: layerId,
                    type: "line",
                    source: sourceId,
                    layout: { "line-join": "round", "line-cap": "round" },
                    paint: {
                        "line-color": color,
                        "line-width": width,
                        "line-opacity": opacity,
                    },
                });

                // Marcadores (solo para rutas seleccionadas o siempre? Mejor siempre pero con menor opacidad)
                // Para simplificar, los mostramos siempre.
                route.stops.forEach((stop, stopIndex) => {
                    if (!isValidCoordinate(stop.lat, stop.lng)) return;
                    const el = document.createElement("div");
                    el.className = "vrp-marker";
                    el.style.width = "28px";
                    el.style.height = "28px";
                    el.style.borderRadius = "50%";
                    el.style.backgroundColor = color;
                    el.style.border = "2px solid white";
                    el.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";
                    el.style.display = "flex";
                    el.style.alignItems = "center";
                    el.style.justifyContent = "center";
                    el.style.color = "white";
                    el.style.fontWeight = "bold";
                    el.style.fontSize = "11px";
                    el.style.cursor = "pointer";
                    el.style.opacity = isSelected ? 1 : 0.4;

                    if (stopIndex === 0) {
                        el.style.backgroundColor = "#2e7d32";
                        el.textContent = "O";
                    } else if (stopIndex === route.stops.length - 1) {
                        el.style.backgroundColor = "#c62828";
                        el.textContent = "D";
                    } else {
                        el.textContent = String(stopIndex);
                    }

                    /*const popup = new mapboxgl.Popup({ offset: 25 })
                        .setHTML(`<div><strong>${stop.locnom || 'Parada'}</strong><br/>Pasajeros: ${stop.passengers ? stop.passengers.length : 0}<br/>Orden: ${stopIndex + 1}</div>`);
                    new mapboxgl.Marker(el).setLngLat([stop.lng, stop.lat]).setPopup(popup).addTo(mapInstance);*/
                    // Construir la lista de pasajeros con nombre y destino
                    let passengersHtml = '';                    
                    if (stop.passengers && stop.passengers.length > 0) {
                        passengersHtml = '<ul style="margin:4px 0;padding-left:16px;font-size:12px;list-style-type:disc;">';
                        stop.passengers.forEach(p => {
                            const name = `${p.pernom || ''} ${p.perape || ''}`.trim() || 'Sin nombre';
                            const address = p.destinonomb || 'Sin dirección';
                            passengersHtml += `<li>${name} – ${address}</li>`;
                        });
                        passengersHtml += '</ul>';
                    } else {
                        passengersHtml = '<span style="color:#888;font-size:12px;">Sin pasajeros</span>';
                    }

                    const popup = new mapboxgl.Popup({ offset: 25 })
                        .setHTML(`<div><strong>${stop.locnom || 'Parada'}</strong><br/>${passengersHtml}</div>`);
                    new mapboxgl.Marker(el).setLngLat([stop.lng, stop.lat]).setPopup(popup).addTo(mapInstance);
                });
            } catch (e) { console.warn(`Error dibujando ruta ${index}:`, e); }
        });

        // Marcador de origen
        if (origin && isValidCoordinate(origin.lat, origin.lng)) {
            try {
                const originEl = document.createElement("div");
                originEl.className = "vrp-origin-marker";
                originEl.style.width = "36px";
                originEl.style.height = "36px";
                originEl.style.borderRadius = "50%";
                originEl.style.backgroundColor = "#0d47a1";
                originEl.style.border = "3px solid #ffcc00";
                originEl.style.boxShadow = "0 2px 8px rgba(0,0,0,0.4)";
                originEl.style.display = "flex";
                originEl.style.alignItems = "center";
                originEl.style.justifyContent = "center";
                originEl.style.color = "white";
                originEl.style.fontWeight = "bold";
                originEl.style.fontSize = "14px";
                originEl.textContent = "T";
                const popup = new mapboxgl.Popup({ offset: 25 })
                    .setHTML(`<div><strong>${origin.nom || 'Punto de Transbordo'}</strong><br/>Origen de todas las rutas</div>`);
                new mapboxgl.Marker(originEl).setLngLat([origin.lng, origin.lat]).setPopup(popup).addTo(mapInstance);
            } catch (e) { console.warn("Error agregando marcador de origen:", e); }
        }

        // Ajustar bounds
        try {
            const bounds = new mapboxgl.LngLatBounds();
            let hasBounds = false;
            validRoutes.forEach(route => {
                route.stops.forEach(stop => {
                    if (isValidCoordinate(stop.lat, stop.lng)) {
                        bounds.extend([stop.lng, stop.lat]);
                        hasBounds = true;
                    }
                });
            });
            if (origin && isValidCoordinate(origin.lat, origin.lng)) {
                bounds.extend([origin.lng, origin.lat]);
                hasBounds = true;
            }
            destinations.forEach(d => {
                if (isValidCoordinate(d.lat, d.lng)) {
                    bounds.extend([d.lng, d.lat]);
                    hasBounds = true;
                }
            });
            if (hasBounds) {
                mapInstance.fitBounds(bounds, { padding: { top: 40, bottom: 40, left: 40, right: 40 }, maxZoom: 15 });
            }
        } catch (e) { console.warn("Error ajustando bounds:", e); }

    }, [routes, origin, destinations, mapLoaded, selectedRouteIndices]);

    // Leyenda con interacción
    const renderLegend = () => {
        const validRoutes = routes.filter(route =>
            route.stops && route.stops.length >= 2 &&
            route.stops.some(stop => isValidCoordinate(stop.lat, stop.lng))
        );
        if (validRoutes.length === 0) return null;

        return (
            <Paper
                elevation={2}
                sx={{
                    position: 'absolute',
                    bottom: 20,
                    left: 20,
                    zIndex: 10,
                    p: 1.5,
                    maxWidth: 250,
                    maxHeight: 300,
                    overflow: 'auto',
                    bgcolor: 'rgba(255,255,255,0.95)',
                    borderRadius: 2,
                }}
            >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    <LayersIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Rutas
                </Typography>
                <Stack spacing={0.8}>
                    {validRoutes.map((route, index) => {
                        const color = ROUTE_COLORS[index % ROUTE_COLORS.length];
                        const isSelected = selectedRouteIndices.includes(index);
                        const passengers = route.total_passengers || 0;
                        return (
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                key={index}
                                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }, p: 0.5, borderRadius: 1 }}
                                onClick={() => onToggleRoute && onToggleRoute(index)}
                            >
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 4,
                                        bgcolor: color,
                                        borderRadius: 2,
                                        opacity: isSelected ? 1 : 0.3,
                                    }}
                                />
                                <Typography variant="caption" sx={{ flex: 1, fontWeight: isSelected ? 700 : 400 }}>
                                    Vehículo {index + 1} ({passengers} pax)
                                </Typography>
                                {isSelected ? <VisibilityIcon fontSize="small" color="primary" /> : <VisibilityOffIcon fontSize="small" color="disabled" />}
                            </Stack>
                        );
                    })}
                </Stack>
            </Paper>
        );
    };

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />
            {renderLegend()}
        </Box>
    );
};

export default VrpRouteMap;